import { useEffect, useMemo, useRef, useState } from "react";
import { endpoints } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { computeROM, formatCurrency } from "../../lib/romEngine";
import { computeCleaning } from "../../lib/tmEngine";
import { geocodeAddress } from "../../lib/geocode";
import { BLANK, hasMinimumInputs, haversineMiles, isPrimeAreaLocation } from "./helpers";
import { TotalCard, EmptyTotalCard } from "./TotalCard";
import { CleaningTotalCard } from "./CleaningPanel";
import { DeliverablesBlock } from "./DeliverablesBlock";
import { ProposalModal } from "./ProposalModal";
import { RoomBuildUp } from "./RoomBuildUp";
import { SiteConditions } from "./SiteConditions";
import { VoiceDictation } from "./VoiceDictation";
import { MobileTotalBar } from "./MobileTotalBar";
import { Toggle } from "./primitives";
import {
    Sparkle, PaperPlaneTilt, CircleNotch, ArrowsLeftRight, Robot, User, Lightning, FilePdf, MapPin, WifiSlash, Microphone,
} from "@phosphor-icons/react";

const DEMO_LABEL = { "": "no demo", "2ft": "2 ft cut", "4ft": "4 ft cut", full_gut: "full gut" };

/* Short, deterministic confirmation of what a voice-dictated message just
 * captured — computed from the actual inputs (never the LLM's own words), so
 * it's safe to read back on-site. */
function buildReadback(inputs) {
    const rooms = inputs.rooms || [];
    const siteConditions = inputs.siteConditions || [];
    const totalSqft = rooms.length ? rooms.reduce((s, r) => s + (Number(r.sqft) || 0), 0) : Number(inputs.sqft) || 0;
    const demoCounts = {};
    rooms.forEach((r) => { const d = r.demoLevel || ""; demoCounts[d] = (demoCounts[d] || 0) + 1; });
    const demoSummary = Object.entries(demoCounts).map(([k, v]) => `${v} ${DEMO_LABEL[k] || k}`).join(", ");
    const parts = [`${rooms.length} room${rooms.length === 1 ? "" : "s"} captured · ${totalSqft.toLocaleString()} sq ft total`];
    if (demoSummary) parts.push(`demo: ${demoSummary}`);
    if (siteConditions.length) parts.push(`${siteConditions.length} site condition${siteConditions.length === 1 ? "" : "s"} noted`);
    return "Readback — " + parts.join(" · ");
}

const GREETING =
    "Hi — I'm Restore AI, your estimator. Describe the job in plain English and I'll build the ROM for you. " +
    "Tell me the loss type, rough square footage and how heavy the damage is, plus anything else you know " +
    "(address, water category, number of units, drying days). I'll ask if I need more.";

const EXAMPLES = [
    "Cat 2 water loss, ~4,500 sq ft commercial office, moderate damage, single story, 7 drying days",
    "House fire, 2,200 sq ft single-family residential, heavy smoke throughout, full gut on the kitchen",
    "Storm/flood, 3 units in a multifamily building, ~1,800 sq ft each, Cat 3 water",
];

/* Build a numbered list of the current line items so the AI can resolve
 * "delete row 5" / "change row 3" style commands. */
function buildLineList(result) {
    if (!result) return [];
    const out = [];
    let n = 1;
    (result.labour_lines || []).forEach((l) => {
        out.push(`${n++}. [Labour] ${l.role} — ${l.headcount ?? "?"} crew, ${formatCurrency(l.subtotal)}`);
    });
    (result.equipment_lines || []).forEach((l) => {
        out.push(`${n++}. [Equipment] ${l.item} — qty ${l.qty} × ${l.days}d, ${formatCurrency(l.subtotal)}`);
    });
    (result.subcontractor_lines || []).forEach((l) => {
        out.push(`${n++}. [Subcontractor] ${l.item || l.label} — ${formatCurrency(l.subtotal)}`);
    });
    const bi = result.breakdown_inputs || {};
    const miscMap = { per_diem: bi.per_diem_total, mobilisation: bi.mobilisation, permits: bi.permits, disposal: bi.disposal };
    Object.entries(miscMap).forEach(([k, v]) => {
        if (v) out.push(`${n++}. [Misc] ${k} — ${formatCurrency(v)}`);
    });
    return out;
}

export function AIEstimator({ active, schedule, vendorStats, calibBands, franchiseHQ, refLabel, onOpenInEditor, onInputsChange, editingJobId, jobReady, savedInputs }) {
    const { online } = useAuth();
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
    const [draft, setDraft] = useState("");
    const [draftIsVoice, setDraftIsVoice] = useState(false);
    const [sending, setSending] = useState(false);
    const [showProposal, setShowProposal] = useState(false);
    const [inputFocused, setInputFocused] = useState(false);
    const [aiInputs, setAiInputs] = useState({ ...BLANK, mode: "rom" });
    const scrollRef = useRef(null);
    const taRef = useRef(null);
    const lastGeoAddr = useRef("");
    const restoredKeyRef = useRef(null);

    /* Restore (or create) the chat session once the parent has finished loading
     * the job we're editing (or confirmed there's no job to load). This is what
     * survives a full-page reload: if the saved job already carries a
     * `session_id` (persisted below), fetch its transcript from the backend and
     * re-hydrate both the visible chat log and the live estimate inputs instead
     * of resetting to a blank greeting. Guarded by `restoredKeyRef` so it only
     * runs once per job — not on every keystroke-driven `inputs` change. */
    useEffect(() => {
        if (!jobReady) return;
        const key = editingJobId || "new";
        if (restoredKeyRef.current === key) return;
        restoredKeyRef.current = key;

        const existingSessionId = savedInputs?.session_id;
        if (existingSessionId && existingSessionId === sessionId) {
            // Same session already live in this mounted instance — e.g. the
            // /calculator/new -> /calculator/{id} URL promotion that fires right
            // after the very first auto-save. Nothing to restore; re-fetching
            // here would clobber the in-progress chat with the (shorter) saved
            // transcript. See PRD "AI chat wiped on first message" fix.
            return;
        }
        if (existingSessionId) {
            setSessionId(existingSessionId);
            if (savedInputs?.mode === "ai") setAiInputs({ ...BLANK, ...savedInputs });
            (async () => {
                try {
                    const { data } = await endpoints.aiEstimateSession(existingSessionId);
                    const hist = data?.messages || [];
                    if (hist.length) {
                        setMessages(hist.map((m) => ({ role: m.role, text: m.content })));
                    }
                } catch { /* keep the default greeting if the session can't be fetched */ }
            })();
        } else {
            setSessionId((crypto.randomUUID && crypto.randomUUID()) || `ai-${Date.now()}`);
            setMessages([{ role: "assistant", text: GREETING }]);
            setAiInputs({ ...BLANK, mode: "rom" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobReady, editingJobId]);

    /* Keep the session_id inside aiInputs so it flows up to the parent's
     * `inputs` (via the mirror effect below) and gets persisted on the job —
     * that's the piece that makes restoration possible after a reload. */
    useEffect(() => {
        if (!sessionId) return;
        setAiInputs((prev) => (prev.session_id === sessionId ? prev : { ...prev, session_id: sessionId }));
    }, [sessionId]);

    const autoGrow = () => {
        const el = taRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight, 260) + "px";
    };
    useEffect(autoGrow, [draft]);

    const result = useMemo(() => {
        if (!schedule || !hasMinimumInputs(aiInputs)) return null;
        return computeROM(aiInputs, schedule, calibBands ? { bands: calibBands } : null);
    }, [aiInputs, schedule, calibBands]);

    const isCleaning = aiInputs.mode === "cleaning";
    const cleaningResult = useMemo(
        () => (schedule && isCleaning ? computeCleaning(aiInputs.cleaning, schedule) : null),
        [aiInputs, schedule, isCleaning],
    );

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    /* Mirror the live AI estimate up to the Calculator so the header Actions
     * (Send / Create project / Export) and auto-save work in AI mode too. Only
     * while the AI tab is active, and never on first mount — otherwise mounting
     * (the component now stays mounted across tab switches to preserve the chat)
     * would clobber freshly-loaded job inputs with a blank estimate. */
    const mirrored = useRef(false);
    useEffect(() => {
        if (!active) return;
        if (!mirrored.current) { mirrored.current = true; return; }
        if (onInputsChange) onInputsChange(aiInputs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiInputs]);

    const setField = (key, val) => setAiInputs((p) => ({ ...p, [key]: val }));

    /* Rooms / site-conditions management mirrors Calculator.jsx's manual ROM
     * editor so the same `inputs.rooms`/`inputs.siteConditions` structure
     * carries straight through "Open in ROM editor". Room sqft always feeds
     * the top-level sqft here (no manual override toggle in AI mode — the
     * whole point is the AI/voice flow keeps it in sync automatically). */
    const rooms = aiInputs.rooms || [];
    const roomTotal = rooms.reduce((s, r) => s + (Number(r.sqft) || 0), 0);
    const applyRooms = (next) => {
        const total = next.reduce((s, r) => s + (Number(r.sqft) || 0), 0);
        setAiInputs((p) => ({ ...p, rooms: next, ...(total > 0 ? { sqft: total } : {}) }));
    };
    const addRoom = () => applyRooms([...rooms, { id: crypto.randomUUID(), name: "", floor: "", sqft: "", notes: "", condition: "", demoLevel: "", materials: "" }]);
    const updateRoom = (id, key, val) => applyRooms(rooms.map((r) => (r.id === id ? { ...r, [key]: val } : r)));
    const removeRoom = (id) => applyRooms(rooms.filter((r) => r.id !== id));

    const siteConditions = aiInputs.siteConditions || [];
    const addSiteCondition = () => setField("siteConditions", [...siteConditions, { text: "", type: "other" }]);
    const updateSiteCondition = (idx, key, val) => setField("siteConditions", siteConditions.map((c, i) => {
        if (i !== idx) return c;
        const base = typeof c === "string" ? { text: c, type: "other" } : (c || {});
        return { ...base, [key]: val };
    }));
    const removeSiteCondition = (idx) => setField("siteConditions", siteConditions.filter((_, i) => i !== idx));

    /* Geocode an AI-provided address and auto-apply Far-Job mileage + Prime Area,
     * so the assistant never has to ask about travel distance. */
    async function maybeDetectDistance(addr) {
        const clean = (addr || "").trim();
        if (!clean || clean === lastGeoAddr.current) return;
        if (!franchiseHQ?.hq_address_parts?.lat) return;
        lastGeoAddr.current = clean;
        const parts = await geocodeAddress(clean);
        if (!parts?.lat) return;
        const miles = haversineMiles(
            franchiseHQ.hq_address_parts.lat, franchiseHQ.hq_address_parts.lng, parts.lat, parts.lng,
        );
        // Far Job trigger: >100 mi OR out of state.
        const outOfState = !!(parts.state && franchiseHQ.hq_address_parts.state
            && parts.state !== franchiseHQ.hq_address_parts.state);
        const far = miles > 100 || outOfState;
        const prime = isPrimeAreaLocation(parts);
        setAiInputs((prev) => ({
            ...prev,
            address: parts.full || prev.address,
            address_parts: parts,
            distance_miles: Math.round(miles),
            outOfState,
            farJob: far,
            ...(prime !== null ? { primeArea: prime } : {}),
        }));
        const where = parts.city ? `${parts.city}${parts.state ? ", " + parts.state : ""}` : clean;
        setMessages((m) => [...m, {
            role: "assistant", system: true, kind: "location",
            text: `Location detected: ${where} — about ${Math.round(miles)} mi from your office. `
                + (far ? `Far-Job mileage has been applied automatically${outOfState && miles <= 100 ? " (out of state)" : ""}.`
                       : "Within local range, so no far-job premium is added.")
                + (prime === true ? " Prime-area labour rates also applied." : ""),
        }]);
    }

    async function send(text, opts = {}) {
        const message = (text ?? draft).trim();
        if (!message || sending || !online || !sessionId) return;
        const isVoice = !!opts.voice;
        setDraft("");
        setMessages((m) => [...m, { role: "user", text: message }]);
        setSending(true);
        try {
            const { data } = await endpoints.aiEstimateChat({
                session_id: sessionId,
                message,
                current_inputs: aiInputs,
                current_lines: buildLineList(result),
            });
            setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
            if (data.inputs && typeof data.inputs === "object") {
                const nextMode = data.inputs.mode === "cleaning" ? "cleaning" : "rom";
                const incoming = { ...data.inputs };
                if (Array.isArray(incoming.rooms)) {
                    // Ensure every room has a stable id (the AI may omit one for a
                    // brand-new room) and never trust the LLM's own arithmetic for
                    // the dollar-moving sqft total — recompute it deterministically.
                    incoming.rooms = incoming.rooms.map((r) => ({ id: r.id || crypto.randomUUID(), ...r }));
                    const total = incoming.rooms.reduce((s, r) => s + (Number(r.sqft) || 0), 0);
                    if (total > 0) incoming.sqft = total;
                }
                setAiInputs((prev) => ({ ...BLANK, ...prev, ...incoming, mode: nextMode }));
                maybeDetectDistance(data.inputs.address);
                if (isVoice) {
                    setMessages((m) => [...m, { role: "assistant", system: true, text: buildReadback(incoming) }]);
                }
            }
        } catch (e) {
            setMessages((m) => [...m, {
                role: "assistant",
                text: "Sorry — I hit a problem reaching the AI. Please try again in a moment.",
            }]);
        } finally {
            setSending(false);
            setDraftIsVoice(false);
        }
    }

    function onVoiceTranscript(text, opts = {}) {
        if (opts.recovered) {
            setMessages((m) => [...m, {
                role: "assistant", system: true, kind: "recovered",
                text: "Recovered from an earlier offline capture — added to your message box below.",
            }]);
        }
        setDraft((d) => (d.trim() ? `${d.trim()}\n${text}` : text));
        setDraftIsVoice(true);
        taRef.current?.focus();
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-24 sm:pb-0" data-testid="ai-estimator">
            {/* LEFT — chat */}
            <div className="lg:col-span-7 no-print">
                <div className="rom-card flex flex-col h-[75vh] sm:h-[min(72vh,760px)]">
                    <div className="rom-card-head flex items-center justify-between">
                        <h3 className="flex items-center gap-2">
                            <Sparkle size={16} weight="fill" className="text-[var(--rom-orange)]" /> Restore AI Estimator
                        </h3>
                        <span className="text-[11px] text-[var(--rom-text-3)]">Claude · powered by Beyond Restoration</span>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="ai-chat-log">
                        {messages.map((m, i) => (
                            m.system ? (
                                <div key={i} className="flex justify-center" data-testid={`ai-msg-system-${i}`}>
                                    <div className="flex items-center gap-1.5 text-[11px] text-[var(--rom-text-2)] bg-[var(--rom-orange-soft)]/50 border border-[var(--rom-orange)]/30 rounded-full px-3 py-1.5 max-w-[90%]">
                                        {m.kind === "recovered"
                                            ? <Microphone size={12} weight="fill" className="text-[var(--rom-orange)] shrink-0" />
                                            : <MapPin size={12} weight="fill" className="text-[var(--rom-orange)] shrink-0" />}
                                        {" "}{m.text}
                                    </div>
                                </div>
                            ) : (
                            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${m.role}-${i}`}>
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-[var(--rom-navy)]" : "bg-[var(--rom-orange)]"}`}>
                                    {m.role === "user" ? <User size={14} weight="bold" className="text-white" /> : <Robot size={14} weight="bold" className="text-white" />}
                                </div>
                                <div className={`max-w-[82%] rounded-[10px] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--rom-navy)] text-white" : "bg-slate-100 text-[var(--rom-text)]"}`}>
                                    {m.text}
                                </div>
                            </div>
                            )
                        ))}
                        {sending && (
                            <div className="flex gap-3" data-testid="ai-thinking">
                                <div className="w-7 h-7 rounded-full bg-[var(--rom-orange)] flex items-center justify-center shrink-0">
                                    <Robot size={14} weight="bold" className="text-white" />
                                </div>
                                <div className="bg-slate-100 rounded-[10px] px-3.5 py-2.5 text-sm text-[var(--rom-text-2)] flex items-center gap-2">
                                    <CircleNotch size={13} className="animate-spin" /> Thinking…
                                </div>
                            </div>
                        )}
                    </div>

                    {!online && (
                        <div className="px-5 pb-3" data-testid="ai-offline-banner">
                            <div className="flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-[6px] px-3 py-2.5">
                                <WifiSlash size={15} weight="bold" className="shrink-0 text-amber-700" />
                                AI Estimate needs a live connection. Use Sq-ft ROM or T&amp;M to keep working offline.
                            </div>
                        </div>
                    )}

                    {messages.length <= 1 && (
                        <div className="px-5 pb-2 flex flex-wrap gap-2" data-testid="ai-examples">
                            {EXAMPLES.map((ex, i) => (
                                <button key={i} onClick={() => send(ex)} disabled={sending || !online}
                                    className="text-[11px] text-left px-2.5 py-1.5 rounded-[6px] border border-[var(--rom-border)] text-[var(--rom-text-2)] hover:border-[var(--rom-orange)] hover:text-[var(--rom-text)] transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid={`ai-example-${i}`}>
                                    <Lightning size={11} weight="fill" className="text-[var(--rom-orange)] shrink-0" /> {ex}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="border-t border-[var(--rom-border)] p-3 flex items-end gap-2">
                        <textarea
                            ref={taRef}
                            value={draft}
                            onChange={(e) => { const v = e.target.value; setDraft(v); if (!v.trim()) setDraftIsVoice(false); }}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(undefined, { voice: draftIsVoice }); } }}
                            onFocus={() => setInputFocused(true)}
                            onBlur={() => setInputFocused(false)}
                            rows={1}
                            disabled={!online}
                            placeholder={online
                                ? "Describe the job, dictate a walkthrough with the mic, or ask to adjust (e.g. 'add 3 air movers for 7 days', 'delete row 5')…  ·  Enter to send, Shift+Enter for a new line"
                                : "AI Estimate is unavailable offline — switch to Sq-ft ROM or T&M above."}
                            className="flex-1 resize-none px-3 py-2.5 rounded-[8px] border border-[var(--rom-border)] text-sm leading-relaxed focus:border-[var(--rom-orange)] focus:outline-none min-h-[44px] sm:min-h-[120px] max-h-[260px] overflow-y-auto disabled:bg-slate-50 disabled:cursor-not-allowed"
                            data-testid="ai-input"
                        />
                        <VoiceDictation onTranscript={onVoiceTranscript} disabled={!sessionId} sessionId={sessionId} />
                        <button onClick={() => send(undefined, { voice: draftIsVoice })} disabled={sending || !draft.trim() || !online || !sessionId} title={!online ? "AI Estimate needs a live connection" : undefined} className="btn btn-primary h-10 px-4 self-end" data-testid="ai-send">
                            {sending ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneTilt size={15} weight="bold" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* RIGHT — live preview */}
            <div className="lg:col-span-5">
                <div className="lg:sticky lg:top-4 space-y-4">
                    {isCleaning ? (
                        <>
                            <CleaningTotalCard result={cleaningResult || { grand_total: 0, lines: [], groups: {} }} />
                            <button className="btn btn-primary w-full justify-center" onClick={() => onOpenInEditor(aiInputs)} data-testid="ai-open-cleaning-editor">
                                <ArrowsLeftRight size={14} weight="bold" /> Open in Cleaning editor to fine-tune & save
                            </button>
                        </>
                    ) : result ? (
                        <>
                            <TotalCard result={result} inputs={aiInputs} ref_={refLabel} />
                            <button className="btn btn-primary w-full justify-center" onClick={() => setShowProposal(true)} data-testid="ai-generate-proposal">
                                <FilePdf size={14} weight="bold" /> Generate client proposal (PDF)
                            </button>
                            <button className="btn btn-ghost w-full justify-center" onClick={() => onOpenInEditor(aiInputs)} data-testid="ai-open-in-editor">
                                <ArrowsLeftRight size={14} weight="bold" /> Open in ROM editor to fine-tune & save
                            </button>
                        </>
                    ) : (
                        <EmptyTotalCard inputs={aiInputs} />
                    )}
                    {!isCleaning && (
                        <Toggle
                            label="Reconstruction prep"
                            desc={aiInputs.includeRecon
                                ? "Mitigation + recon — set from this chat, flip here to override"
                                : "Mitigation only — set from this chat, flip here to override"}
                            value={!!aiInputs.includeRecon}
                            onChange={(v) => setField("includeRecon", v)}
                            testId="ai-toggle-recon"
                        />
                    )}
                </div>
            </div>

            {/* Rooms & site conditions — populated live by voice dictation or manual entry */}
            {!isCleaning && (
                <div className="lg:col-span-12 no-print">
                    <RoomBuildUp rooms={rooms} roomTotal={roomTotal} sqftOverride={false} onAdd={addRoom} onUpdate={updateRoom} onRemove={removeRoom} />
                    <SiteConditions conditions={siteConditions} onAdd={addSiteCondition} onUpdate={updateSiteCondition} onRemove={removeSiteCondition} />
                </div>
            )}

            {/* Full editable breakdown once we have a result */}
            {result && (
                <div className="lg:col-span-12 no-print">
                    <DeliverablesBlock result={result} inputs={aiInputs} schedule={schedule} setField={setField} vendorStats={vendorStats} />
                </div>
            )}

            <ProposalModal
                open={showProposal}
                onClose={() => setShowProposal(false)}
                result={result}
                inputs={aiInputs}
                refLabel={refLabel}
            />

            {!isCleaning && (
                <MobileTotalBar
                    result={result}
                    inputs={aiInputs}
                    hidden={inputFocused}
                    onOpenEditor={() => onOpenInEditor(aiInputs)}
                    onGenerateProposal={() => setShowProposal(true)}
                />
            )}
        </div>
    );
}
